import { useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';

import { Button, type ButtonVariant } from '@clay-run/glaze/button';
import { Popover, PopoverButton, PopoverPanel } from '@clay-run/glaze/popover';
import { Text } from '@clay-run/glaze/text';
import { addErrorToast } from '@clay-run/glaze/toast/toast-queue';

import { useApi } from '../../_shared/hooks';
import { getSequenceUsage } from '../api-client';
import { isVersionConflictError } from '../conflict-errors';
import { ensurePlayOwned, gateInPlayWrite } from '../ensure-play-owned';
import { isSharedMappingConflictError, setSequenceSender } from '../mutations';
import { sequenceUsageQueryKey } from '../query-keys';
import { applySequenceSenderToCache } from '../sender-decision';
import {
  resolveSenderChoice,
  senderChoiceFromSender,
  STATIC_EMAIL_SENDER_OPTION,
  type SenderChoice,
} from '../sender-choice';
import { SEQUENCE_UNAVAILABLE_NAME } from '../sequence-card-menu';
import { GROUP_MOVED_SENTENCE, usedInPlacesText } from '../usage-gate';
import { settingsDraftForReopen } from '../settings-draft-restore';
import {
  openUsageSidebarFromUsage,
  useUsageSidebarStore,
  type SequenceSettingsDraft,
  type UsageSidebarOrigin,
} from '../usage-sidebar-store';
import { SenderChoiceFields } from './play-flow/SenderChoiceFields';

/** The injected throwing `apiFetch` (`_shared/hooks/useApi.ts`). */
type ApiFetch = (input: RequestInfo, init?: RequestInit) => Promise<Response>;

/**
 * Plan 2026-09-28-006 U4 (rulebook R5, R6, R3) — the ONE usage gate every
 * sequence SETTINGS edit goes through: sender (this popover), CC/reply-to
 * (`SequenceInflectionPopover`), flow id (`SequenceFlowIdPopover`) and
 * rename (`SequencesStep.tsx#handleRenameSave`). Gate by USE, never by
 * ownership:
 *
 *  1. fetch usage fresh (`staleTime: 0`);
 *  2. {@link gateInPlayWrite} (`ensure-play-owned.ts`) decides — a group
 *     origin classifies by use; a Library origin decides by
 *     `libraryUsageGateNeedsConfirmation` instead. A group with no saved id
 *     yet (`rungId === null`) throws `SAVE_GROUP_FIRST` before any request —
 *     see that function's own doc for the play/playbook distinction;
 *  3. the Save panel's input is built ONCE (target with `owningPlayId` /
 *     `archivedInLibrary`, origin, `draftSettings`, and `places` /
 *     `ownVersionPlays` / `templatePlaybooks` off the SAME fresh usage fetch
 *     step 1 made) and used by every branch below;
 *  4. `'menu'` → the Save menu opens with it; nothing is written.
 *     `{ kind: 'menu' }`.
 *  5. `'stale'` (plan 2026-09-28-008 U2, R14 — the group was changed by
 *     someone else since the rep loaded it) → the SAME input opens already
 *     stale with {@link GROUP_MOVED_SENTENCE}: no request, no reload, the
 *     rep's value rides on the pending until they press "Reload to see
 *     their change". `{ kind: 'stale' }`.
 *  6. `'silent'` → `write(id, group)` on the right id: in a play, an OWNED
 *     sequence is written in place and `write` receives `{ rungId }` (the
 *     group it was saved from — `write` forwards it, so the server can
 *     refuse a group that no longer names the copy); a Library sequence
 *     first forks for THIS group only (`ensurePlayOwned` with the origin's
 *     `rungId`, `mode: 'ensure'`) and the write lands on the copy. Outside a
 *     play the addressed id is written as-is (doorways unchanged). A 409
 *     `version_conflict` from the fork or the write opens the SAME input
 *     stale with the server's sentence instead of toasting — never a
 *     refetch, never an adopt. `{ kind: 'stale' }`.
 *
 * Plan 2026-09-29-001 U2 (rulebook R14, B5) — the THREE outcomes are named
 * ({@link SequenceSettingsSaveResult}) instead of collapsed into
 * `R | null`. A caller has to treat them differently: `'written'` and
 * `'menu'` both close the control (the value is saved, or it is on the
 * pending and the menu is what the rep acts in), but `'stale'` must NOT —
 * closing the popover on a refusal takes the rep's typed value off the
 * screen at the exact moment they are told someone else got there first.
 *
 * Exported from here (not a new module) because this popover is where the
 * gate was born (round 6, U7); the other two popovers and the rename import
 * it. No network module is mocked around it — the harnesses mock
 * `api-client` / `mutations` / `ensure-play-owned`'s network edge only.
 */
/**
 * Plan 2026-09-29-001 U2 (R14) — what {@link saveSequenceSettingsWithGate}
 * did. `'written'` carries the write's own result; `'menu'` means the Save
 * menu now holds the rep's value and the control may close; `'stale'` means
 * the save was refused and the control must stay open holding what the rep
 * typed, while the Save panel shows the sentence and the one Reload.
 */
export type SequenceSettingsSaveResult<R> =
  | { kind: 'written'; value: R }
  | { kind: 'menu' }
  | { kind: 'stale' };

export async function saveSequenceSettingsWithGate<R>(
  apiFetch: ApiFetch,
  queryClient: QueryClient,
  params: {
    sequenceId: string;
    /** The library row's name — the Save menu's title. */
    sequenceName: string | undefined;
    usageOrigin: UsageSidebarOrigin;
    /** The library row's `owningPlayId` — this play's id for a copy / play-created original. */
    owningPlayId: string | null;
    /** [R18] The library row is a Library piece archived while this play still uses it. */
    archivedInLibrary: boolean;
    draftSettings: SequenceSettingsDraft;
    /** Set in a play: the fork / owned-write branch applies. */
    playId?: string;
    playbookId?: string;
    /**
     * The one write for this field, against the id the gate resolved.
     * `group` is set on an owned in-play write only — forward it
     * (`{ playId, group }`) so the server checks the group still names it.
     * Plan 2026-09-29-001 U1 (R3/R5/R14) — when given, `group` always
     * carries `confirmedRungIds` too: a silent write is sole-use by
     * construction (the gate above already refused otherwise), so the set
     * it was checked against is exactly the one group making it.
     */
    write: (sequenceId: string, group?: { rungId: string; confirmedRungIds?: string[] }) => Promise<R>;
  },
): Promise<SequenceSettingsSaveResult<R>> {
  const { sequenceId, usageOrigin, playId, playbookId } = params;
  const usage = await queryClient.fetchQuery({
    queryKey: sequenceUsageQueryKey(sequenceId),
    queryFn: () => getSequenceUsage(apiFetch, sequenceId),
    staleTime: 0,
  });
  const owned = playId !== undefined && params.owningPlayId === playId;
  // Built once — the menu, the stale refusal and a silent write's 409 all
  // open this same input (plan 2026-09-28-008 U2).
  const openSavePanel = (staleRefusal?: string) =>
    openUsageSidebarFromUsage(
      usage,
      usageOrigin,
      {
        kind: 'sequence',
        id: sequenceId,
        name: params.sequenceName ?? SEQUENCE_UNAVAILABLE_NAME,
        owningPlayId: params.owningPlayId,
        archivedInLibrary: params.archivedInLibrary,
      },
      {
        draftSteps: null,
        draftSettings: params.draftSettings,
        headVersionId: null,
        ...(staleRefusal === undefined ? {} : { staleRefusal }),
      },
    );
  const gate = gateInPlayWrite({ places: usage.places, origin: usageOrigin });
  if (gate === 'menu') {
    openSavePanel();
    return { kind: 'menu' };
  }
  if (gate === 'stale') {
    openSavePanel(GROUP_MOVED_SENTENCE);
    return { kind: 'stale' };
  }
  try {
    if (playId === undefined || playbookId === undefined) {
      return { kind: 'written', value: await params.write(sequenceId) };
    }
    // `gateInPlayWrite` already threw `SAVE_GROUP_FIRST` for a `'play'`
    // origin with `rungId === null` — every play-mode host supplies a
    // `'play'` origin whenever `playId` is defined (never `'library'`, which
    // has none), so a `'silent'` result here guarantees a real rungId.
    const rungId = (usageOrigin.kind === 'library' ? null : usageOrigin.rungId)!;
    // Plan 2026-09-29-001 U1 (R3/R5/R14) — a silent write also names the
    // group SET it was checked against; a sole-use write is checked against
    // exactly the one group making it.
    if (owned) {
      return { kind: 'written', value: await params.write(sequenceId, { rungId, confirmedRungIds: [rungId] }) };
    }
    const { ownedId } = await ensurePlayOwned(apiFetch, queryClient, {
      playId,
      playbookId,
      kind: 'sequence',
      identityId: sequenceId,
      rungId,
      mode: 'ensure',
    });
    return { kind: 'written', value: await params.write(ownedId, { rungId, confirmedRungIds: [rungId] }) };
  } catch (error) {
    // [R14] refused as stale — the Save panel holds the rep's value; only
    // their Reload refetches. Anything else is the caller's to toast.
    if (isVersionConflictError(error)) {
      openSavePanel(error instanceof Error ? error.message : GROUP_MOVED_SENTENCE);
      return { kind: 'stale' };
    }
    // Plan 2026-09-29-001 U1 (R5/R8) — a second group started sharing the
    // piece between the usage read above and this write: refetch usage
    // fresh and open the Save menu on the CURRENT list, instead of toasting
    // a write the rep never chose to fan out.
    if (isSharedMappingConflictError(error)) {
      const freshUsage = await queryClient.fetchQuery({
        queryKey: sequenceUsageQueryKey(sequenceId),
        queryFn: () => getSequenceUsage(apiFetch, sequenceId),
        staleTime: 0,
      });
      openUsageSidebarFromUsage(
        freshUsage,
        usageOrigin,
        {
          kind: 'sequence',
          id: sequenceId,
          name: params.sequenceName ?? SEQUENCE_UNAVAILABLE_NAME,
          owningPlayId: params.owningPlayId,
          archivedInLibrary: params.archivedInLibrary,
        },
        { draftSteps: null, draftSettings: params.draftSettings, headVersionId: null },
      );
      return { kind: 'menu' };
    }
    throw error;
  }
}

/**
 * Playbooks QA round 4, U9 (SN1) / Decision 3 — the sequence's own sender
 * control, in Config → Sequences.
 *
 * ── WHAT MOVED, AND WHY ───────────────────────────────────────────────────
 * This is `SequencesStep.tsx`'s former `InlineSenderPopover`, relocated and
 * rebound. The UI is unchanged (Glaze `Popover`/`PopoverButton`/`PopoverPanel`
 * + `SenderChoiceFields`, Save-refuses-rather-than-disables); what changed is
 * WHAT it writes. The old one wrote `mapping.sender` on ONE playbook rung via
 * `setPlaybookDraftRungSender` — which is exactly the per-rung override
 * Spencer's decision 3 removed ("sender is locked to the sequence"). This one
 * writes `playbooks_sequence.default_sender` via `setSequenceSender`, so
 * there is one sender per sequence and one place to change it.
 *
 * ── SAVE SEMANTICS (copied verbatim from the popover this replaces) ────────
 * Save stays ENABLED even with an invalid email and REFUSES (shows the error
 * inline, keeps the popover open) rather than disabling itself — a disabled
 * button gives the rep nothing to act on next.
 *
 * `null` is a legal save: clearing the field and saving sets the sequence back
 * to "Not set", which is a real state (Decision 10), not a placeholder.
 * `resolveSenderChoice` reports a BLANK static-email field as an error rather
 * than as a clear, so `handleSave` special-cases "the rep emptied it" before
 * consulting it — the alternative (a separate Clear button) would add a
 * control for something the empty field already expresses.
 *
 * ── LIVE INHERITANCE, NOT COPY-ON-MAP (Playbooks QA round 5, R5-4) ────────
 * A save here is read by EVERY rung mapped to this sequence immediately —
 * `effectiveSender` (`sender-choice.ts`) reads the sequence's current value,
 * not a per-rung copy. Round 4's Decision 11 (copy-on-map) was the bug R5-4
 * fixed: it meant a Config sender edit never reached a rung that had already
 * mapped the sequence. This save still writes exactly ONE row
 * (`playbooks_sequence.default_sender` via `setSequenceSender`) — no ladder
 * write, no version bump, nothing near the bulk-write territory the S1/S2
 * usage gates exist to protect against — the "changes everywhere" is a read
 * consequence of live inheritance, not a fan-out write this popover performs.
 *
 * ── PLAYBOOKS QA ROUND 6, U7 (ITEM 3) — GATED THROUGH THE USAGE SIDEBAR ────
 * R5-5's plain inline warning ("Used in N places — changing the sender
 * updates it everywhere", `senderWarningText`, now DELETED) was a stopgap:
 * once a rep is offered a real CHOICE ("update everywhere" vs. "fork a new
 * sequence"), a warning caption that only ever describes the "update
 * everywhere" branch is misleading. This now fetches usage FRESH
 * (`staleTime: 0`) on Save and, above the SAME per-origin thresholds every
 * other gate in this app uses (`inPlayUsageGate` for a rung
 * origin, `libraryUsageGateNeedsConfirmation` for the Library doorway — see
 * `SegmentationConditionsEditor.tsx#saveWithGate`'s identical shape), opens
 * the "Where is this used" sidebar with a `draftSender` pending instead of
 * writing directly. Below the threshold (the sole-use case), this still
 * performs today's plain write, silently, no dialog — that must never
 * change (round-3-QA-fixes' "never confirm-every-save" rule).
 *
 * `usageOrigin`/`sequenceName` are REQUIRED (not defensively optional): both
 * call sites (`SequencesStep.tsx`'s play-flow header, `PlaybooksConfigPage.tsx`'s
 * Library row) have them, and a popover with nowhere to park a gated
 * decision would strand the rep with a sidebar whose buttons do nothing —
 * the same reasoning `SegmentationConditionsEditor.tsx`'s optional
 * `usageOrigin` documents for why IT tolerates a missing origin (it has
 * hosts that predate the gate) does not apply here: every sender host was
 * built after this gate existed.
 *
 * Local `choice`/`showError` state is plain `useState` (not a draft store) for
 * the same reason the popover it replaces gave: playbooks mounts no
 * `artifact:updated` SSE subscriber, so the draft-state-under-SSE rule's
 * re-render hazard does not apply here.
 */
export function SequenceSenderPopover({
  sequenceId,
  currentSender,
  usageCount,
  variant = 'secondary',
  size = 'xs',
  usageOrigin,
  sequenceName,
  onSaved,
  playId,
  playbookId,
  owningPlayId = null,
  archivedInLibrary = false,
}: {
  sequenceId: string;
  /** The sequence's stored `default_sender`, or `null` if never set. */
  currentSender: string | null;
  /**
   * Playbooks QA round 5, R5-5 — the sequence's rung-count-across-active-
   * playbook-heads (`PlaybooksSequenceSummary.usageCount`), already loaded
   * wherever this is rendered (Config's row, or the play-flow header's
   * `sequenceSummary`). Round 6, U7 — now drives {@link usedInPlacesText}'s
   * caption (the old `senderWarningText`'s "updates it everywhere" wording
   * is no longer true unconditionally once a fork/update-all CHOICE exists).
   */
  usageCount: number;
  /**
   * Playbooks QA round 5, R5-2 — Config's row keeps the original
   * `secondary`/`xs` trigger; the play-flow card header renders this same
   * popover `variant="primary"` (Spencer's L4 ask: "sender button sits next
   * to Generate with AI, primary blue") without a second copy of this
   * component.
   */
  variant?: ButtonVariant;
  size?: 'xs' | 'sm';
  /**
   * Playbooks QA round 6, U7 (item 3) — this save's own "Where is this
   * used" origin, identical shape to every other save gate in the app
   * (`SegmentationConditionsEditor`'s `usageOrigin` prop,
   * `SequenceCopyPane`'s hardcoded library origin). Where a rep triggered
   * this popover decides which origin: the play-flow header passes its
   * rung's origin, the Library row passes a `'library'` origin.
   */
  usageOrigin: UsageSidebarOrigin;
  /** Playbooks QA round 6, U7 — the sidebar's title/subtitle need a name; this sequence's own, from the same summary `usageCount` came from. */
  sequenceName: string;
  /**
   * Fired with the saved value after the write lands — the host patches its
   * own row optimistically (Config's `updateLibraryRow`-style `setQueryData`)
   * so the row's `Sender: X` line updates without waiting for a refetch.
   * Fired only by the sole-use, in-place write below — a GATED save's
   * "Update all"/"Save a new sequence" is executed later, by the host's own
   * usage-sidebar decision handler, which patches cache itself.
   */
  onSaved?: (sender: string | null) => void;
  /**
   * Plan 2026-09-25-002, U7 (Decision 21) — passed from the play flow header
   * (`SequencesStep.tsx`) so this save's `?playId=` lets the server's
   * ownership guard confirm the addressed sequence belongs to this play.
   * `undefined` in the playbook doorway and on the Library row, which send
   * no `playId`. The gated "Update all" write forwards it from `usageOrigin`
   * instead (`sender-decision.ts#executeSenderUpdateAll`) — this prop is
   * only for the sole-use write below.
   */
  playId?: string;
  /**
   * Plan 2026-09-27-006 U7a — required alongside `playId`: the sole-use
   * write below runs `ensurePlayOwned` first (Key Technical Decision 3), a
   * no-op when the addressed sequence is already this play's own (owned, or
   * a play-created original) and a fork when it is still a shared Library
   * row. Unused (and unnecessary) outside a play.
   */
  playbookId?: string;
  /**
   * Plan 2026-09-28-006 U4 (rulebook R5, R6) — the library row's
   * `owningPlayId`: this play's id for a copy or a play-created original,
   * `null` for a Library row. Sets the Save menu's `target.owningPlayId`
   * (owned targets get "Update N groups in this play", never ③) and picks
   * the in-place write on the sole-use path.
   */
  owningPlayId?: string | null;
  /** [R18] The library row is a Library piece archived while this play still uses it (③ hidden). */
  archivedInLibrary?: boolean;
}) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState<SenderChoice>(() => senderChoiceFromSender(currentSender));
  /**
   * Whether to show the validation error yet. An empty field on first open is
   * not a mistake the rep has made — flagging it before they have typed
   * anything (or pressed Save) reads as the popover being broken.
   */
  const [showError, setShowError] = useState(false);
  const [saving, setSaving] = useState(false);

  /** The rep emptied (or never filled) the literal-address field: a clear, not an error. */
  const isClear = choice.option === STATIC_EMAIL_SENDER_OPTION && choice.email.trim() === '';
  const resolved = resolveSenderChoice(choice);
  const error = isClear || resolved.ok ? null : resolved.error;

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) {
      // Re-seed from the stored sender every time the popover opens, so a
      // cancelled edit never leaks into the next one.
      //
      // Plan 2026-09-29-001 U2 (R14, B5) — unless a Save panel for THIS
      // group and THIS field is still pending: then the rep's own unsaved
      // sender is what they should see, not the saved one they already
      // decided to change. See `settings-draft-restore.ts`.
      const pendingDraft = settingsDraftForReopen(
        useUsageSidebarStore.getState().pending,
        usageOrigin,
        'sender',
      );
      setChoice(
        senderChoiceFromSender(pendingDraft?.field === 'sender' ? pendingDraft.sender : currentSender),
      );
      setShowError(false);
    }
    setOpen(nextOpen);
  }

  /**
   * Playbooks QA round 6, U7 (item 3) — the save gate; plan 2026-09-28-006
   * U4 moved its body into {@link saveSequenceSettingsWithGate} (above) so
   * every settings edit shares one gate: fetch usage FRESH, decide by the
   * per-origin threshold, and either write (sole use — inside a play, an
   * owned copy in place or a Library sequence forked for THIS group first)
   * or hand a `{ field: 'sender' }` pending to the Save menu and close.
   */
  async function saveWithGate(next: string | null) {
    const response = await saveSequenceSettingsWithGate(apiFetch, queryClient, {
      sequenceId,
      sequenceName,
      usageOrigin,
      owningPlayId,
      archivedInLibrary,
      draftSettings: { field: 'sender', sender: next },
      playId,
      playbookId,
      // Keyed by the id actually written (the copy after a fork), not the
      // response. `group` and `playId` are either both present (in play) or
      // both absent (outside one) — matching `SequenceSettingsWriteOptions`'s
      // union exactly.
      write: (id, group) =>
        (group === undefined
          ? setSequenceSender(apiFetch, id, next, { playId: undefined })
          : setSequenceSender(apiFetch, id, next, { playId: playId!, group })
        ).then((saved) => ({ id, sender: saved.sender })),
    });
    if (response.kind === 'written') {
      applySequenceSenderToCache(queryClient, response.value.id, response.value.sender);
      onSaved?.(response.value.sender);
    }
    // A gated save's value now rides on the store's pending request — the
    // popover is the terminal caller and closes, same as every other gate in
    // this app once it has handed a pending request over.
    //
    // Plan 2026-09-29-001 U2 (R14, B5) — except on a REFUSAL: the save did
    // not happen and the rep has to decide what to do next, so the popover
    // stays open holding exactly what they typed while the Save panel shows
    // the sentence and the Reload.
    if (response.kind !== 'stale') setOpen(false);
  }

  function handleSave() {
    if (error !== null) {
      setShowError(true);
      return;
    }
    const next = isClear ? null : resolved.ok ? resolved.sender : null;
    setSaving(true);
    void saveWithGate(next)
      .catch((err: unknown) => {
        addErrorToast({
          content: err instanceof Error ? err.message : 'Could not update the sender — try again.',
        });
      })
      .finally(() => {
        setSaving(false);
      });
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      {/* `data-row-control` — Config's `RowShell` toggles expand/collapse on a
          header click and skips anything carrying this attribute (see
          `row-click-guard.ts#isInteractiveRowTarget`), so opening the sender
          popover must not also expand the row. */}
      <PopoverButton variant={variant} size={size} data-row-control>
        {currentSender === null ? 'Set sender' : 'Change sender'}
      </PopoverButton>
      <PopoverPanel positioner={{ side: 'bottom', align: 'start' }}>
        <div className="flex w-72 flex-col gap-4 p-3" data-row-control>
          {/* Round 6, U7 — replaces R5-5's `senderWarningText` ("changing the
              sender updates it everywhere" is no longer unconditionally true
              now that a fork/update-all choice exists). Same discoverability
              caption every other usage badge/footer uses; `null` at ≤1 place,
              same as everywhere else. */}
          {usedInPlacesText(usageCount) !== null && (
            <Text size="xs" variant="tertiary">
              {usedInPlacesText(usageCount)}
            </Text>
          )}
          <SenderChoiceFields value={choice} onChange={setChoice} error={showError ? error : null} />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" loading={saving} onClick={handleSave}>
              Save sender
            </Button>
          </div>
        </div>
      </PopoverPanel>
    </Popover>
  );
}
